import React, { useEffect, useState, useMemo } from 'react';
import { Button, message } from 'antd';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { DELETE_ERROR, FETCH_ERROR, SUCCESS_DELETE } from 'constants/alerts';
import { useDrawers } from 'hooks/useDrawers';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { AppVersionService } from 'services/AppVersionService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { AccessControl } from 'components/AccessControl';
import { PlusOutlined } from '@ant-design/icons';
import { AppVersionTable } from './AppVersionTable';
import { AppVersionForm } from './AppVersionForm';

const PAGE_TITLE = 'Версии моб. приложения';

function AppVersion() {
  useDocumentTitle(PAGE_TITLE);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();

  useEffect(() => {
    fetchAppVersions();
  }, [page, perPage]);

  const fetchAppVersions = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await AppVersionService.getAppVersions({
        page,
        perPage,
      });
      setItems(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const recordFields = useMemo(() => {
    return [
      { label: 'Версия приложения', key: 'version' },
      {
        label: 'Заголовок',
        key: 'message',
      },
      { label: 'Заголовок (кз)', key: 'message_kz' },
      { label: 'Описание', key: 'change_log' },
      { label: 'Описание (кз)', key: 'change_log_kz' },
      {
        label: 'SOFT обновление',
        key: 'canEscape',
        computedValue: activeRecord?.canEscape ? 'Да' : 'Нет',
      },
    ];
  }, [activeRecord]);

  const handleDelete = async () => {
    try {
      const isDeleted = await AppVersionService.deleteAppVersion(
        activeRecord?.id
      );

      if (isDeleted) {
        closeDrawer();
        message.success(SUCCESS_DELETE);
        fetchAppVersions();
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    }
  };

  return (
    <>
      <ActionsWrapper placeEnd>
        <AccessControl pageId="appVersion" action="create">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateForm}
          >
            Создать
          </Button>
        </AccessControl>
      </ActionsWrapper>

      <AppVersionTable
        items={items}
        loading={loading}
        current={page}
        pageSize={perPage}
        total={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.version}
        recordFields={recordFields}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите удалить эту версию ?"
        pageId="appVersion"
      />

      <AppVersionForm
        isActive={formIsActive}
        initialValues={activeRecord}
        onSuccess={fetchAppVersions}
        onClose={closeForm}
      />
    </>
  );
}

export { AppVersion };
